/**
 * "More" hub — the entry point to every full-feature module that isn't one of the five bottom tabs.
 * Reached from a tile on the Home hub. Each row pushes the module's screen on the (app) stack.
 *
 * NEVER imports @smartbox/* (mobile invariant 1). Design-system primitives only; no raw styles.
 */
import { View, StyleSheet } from 'react-native';
import { useRouter } from 'expo-router';
import { Screen, Text, ListRow } from '../../src/components/ui';
import { theme } from '../../src/theme';

interface Item {
  title: string;
  subtitle: string;
  href: string;
}

const SECTIONS: { heading: string; items: Item[] }[] = [
  {
    heading: 'Assistant & tools',
    items: [
      { title: 'Assistant', subtitle: 'Ask BEAM to do things or answer questions', href: '/(app)/assistant' },
      { title: 'Estimate Draft', subtitle: 'Start or continue an estimate draft', href: '/(app)/estimator' },
      { title: 'Voice Note', subtitle: 'Capture a spoken note', href: '/(app)/voice' },
      { title: 'Import History', subtitle: 'Import your past customers & jobs', href: '/(app)/import' },
    ],
  },
  {
    heading: 'Customers & sales',
    items: [
      { title: 'Customers', subtitle: 'Add and view customers & contacts', href: '/(app)/customers' },
      { title: 'Messages', subtitle: 'Email/SMS, templates & automations', href: '/(app)/messages' },
    ],
  },
  {
    heading: 'Field work',
    items: [
      { title: 'Jobs', subtitle: 'Job list & profitability', href: '/(app)/jobs' },
      { title: 'New task', subtitle: 'Create & assign a task', href: '/(app)/tasks/new' },
      { title: 'Punchlists', subtitle: 'Job completion checklists', href: '/(app)/punchlists' },
      { title: 'Subcontractors', subtitle: 'Trade partners & their logins', href: '/(app)/subcontractors' },
    ],
  },
  {
    heading: 'Materials & money',
    items: [
      { title: 'Supplier Pricing', subtitle: 'Connect price sources & imports', href: '/(app)/supplier-pricing' },
      { title: 'Reports', subtitle: 'P&L, 1099, materials forecast', href: '/(app)/reports' },
    ],
  },
  {
    heading: 'Compliance',
    items: [
      { title: 'Permits & NOC', subtitle: 'Prepare & track (you submit)', href: '/(app)/permits' },
      { title: 'Government', subtitle: 'Solicitations, certs & contracts', href: '/(app)/government' },
    ],
  },
  {
    heading: 'Help',
    items: [
      { title: 'Help Center', subtitle: 'Guides & how-tos for every screen', href: '/(app)/help' },
    ],
  },
];

export default function MoreScreen() {
  const router = useRouter();
  return (
    <Screen scroll>
      {SECTIONS.map((section) => (
        <View key={section.heading} style={styles.section}>
          <Text variant="label" color="secondary">
            {section.heading}
          </Text>
          <View style={styles.list}>
            {section.items.map((item) => (
              <ListRow
                key={item.href}
                title={item.title}
                subtitle={item.subtitle}
                onPress={() => router.push(item.href as never)}
              />
            ))}
          </View>
        </View>
      ))}
    </Screen>
  );
}

const styles = StyleSheet.create({
  section: { gap: theme.spacing.sm },
  list: { gap: theme.spacing.sm },
});
